import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/system';
import { StationaryArea } from '@thive/fddm-types';

import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { ConstellationSpec } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/common/ConstellationSpec';
import { StationaryType } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/stationary/StationaryType';
import { StationaryConnectNeighbors } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/stationary/StationaryConnectNeighbors';
import { Extensions } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/stationary/Extensions';
import { StationaryPoiPreferences } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/stationary/StationaryPoiPreferences';
import { VehicleType } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/common/VehicleType';
import { UserPreferenceName } from '@/modules/userPreferences';
import {
  FIELDS_PADDING_BOTTOM,
  FIELDS_SPACING,
  PANEL_ACCORDION_SX,
} from '@/modules/common/constants/propertyPanel';

interface Props {
  selectedStationaryAreas: StationaryArea[];
}

function StationaryAreaParametersComponent({ selectedStationaryAreas }: Props) {
  const { t } = useTranslation();
  const title =
    selectedStationaryAreas.length === 1
      ? t<string>('interface:properties.stationary_area.title', 'Stationary Area')
      : t<string>(
          'interface:properties.stationary_area.title_plural',
          `Stationary Areas (${selectedStationaryAreas.length})`,
          { amount: selectedStationaryAreas.length },
        );
  return (
    <Accordion
      persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_STATIONARY}
      title={title}
      unmountOnExit
      sx={PANEL_ACCORDION_SX}
    >
      <Stack direction='column' spacing={FIELDS_SPACING} pb={FIELDS_PADDING_BOTTOM}>
        <PropertiesLabel i18nkey='interface:properties.area.stationary_type'>
          <StationaryType selectedStationaryAreas={selectedStationaryAreas} />
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.area.connect_neighbors'>
          <StationaryConnectNeighbors selectedStationaryAreas={selectedStationaryAreas} />
        </PropertiesLabel>
        <Extensions selectedStationaryAreas={selectedStationaryAreas} />
        <Accordion
          persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_STATIONARY_CONSTELLATION_SETTINGS}
          titleKey='interface:properties.sections.constellation_settings'
          unmountOnExit
        >
          <Stack direction='column' spacing={FIELDS_SPACING}>
            <ConstellationSpec
              selectedAreas={selectedStationaryAreas}
              preferenceKey='stationaryArea.constellationSpec'
            />
          </Stack>
        </Accordion>
        <Accordion
          persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_STATIONARY_VEHICLE_SETTINGS}
          titleKey='interface:properties.sections.vehicle_settings'
          unmountOnExit
        >
          <Stack direction='column' spacing={FIELDS_SPACING}>
            <StationaryPoiPreferences selectedStationaryAreas={selectedStationaryAreas} />
            <VehicleType selectedAreas={selectedStationaryAreas} />
          </Stack>
        </Accordion>
      </Stack>
    </Accordion>
  );
}

export const StationaryAreaParameters = memo(StationaryAreaParametersComponent);
